import { useCallback, useMemo } from 'react';
import isEqual from 'fast-deep-equal';

interface UseReportLayoutManagementProps<
  LayoutType,
  ReportContentConfig,
  UpdateReportSetupMutationFn extends (...args: any[]) => any = (...args: any[]) => any,
  SaveOrgLayoutFn extends (...args: any[]) => any = (...args: any[]) => any,
> {
  isEditMode: boolean;
  tempLayout: LayoutType | undefined;
  layout: LayoutType | undefined;
  setTempLayout: (layout: LayoutType | undefined) => void;
  setLayout: (layout: LayoutType) => void;
  reportId: string | undefined;
  isCreate: boolean;
  selectedConfgValues: ReportContentConfig;
  updateReportSetupMutation: UpdateReportSetupMutationFn;
  saveOrgLayout: SaveOrgLayoutFn;
}

export const useReportLayoutManagement = <
  LayoutType,
  ReportContentConfig,
  UpdateReportSetupMutationFn extends (...args: any[]) => any,
  SaveOrgLayoutFn extends (...args: any[]) => any,
>({
  isEditMode,
  tempLayout,
  layout,
  setTempLayout,
  setLayout,
  reportId,
  selectedConfgValues,
  updateReportSetupMutation,
  saveOrgLayout,
  isCreate,
}: UseReportLayoutManagementProps<LayoutType, ReportContentConfig, UpdateReportSetupMutationFn, SaveOrgLayoutFn>) => {
  const hasUnsavedEdits = useMemo(() => {
    if (!isEditMode) {
      return false;
    }
    return !isEqual(tempLayout, layout);
  }, [tempLayout, layout, isEditMode]);

  const discardLayoutChanges = useCallback(() => {
    if (layout) {
      setTempLayout(layout);
    }
  }, [layout, setTempLayout]);

  const saveLayoutForSession = useCallback(() => {
    if (tempLayout) {
      setLayout(tempLayout);
      if (reportId && !isCreate) {
        updateReportSetupMutation({ requestBody: { configData: tempLayout, reportContentConfig: selectedConfgValues } });
      }
    }
  }, [tempLayout, setLayout, reportId, updateReportSetupMutation, selectedConfgValues, isCreate]);

  const saveLayoutForOrg = useCallback(() => {
    if (tempLayout) {
      saveOrgLayout({ requestBody: { configData: tempLayout } });
    }
    saveLayoutForSession();
  }, [tempLayout, saveOrgLayout, saveLayoutForSession]);

  return {
    hasUnsavedEdits,
    discardLayoutChanges,
    saveLayoutForSession,
    saveLayoutForOrg,
  };
};
